import { FC, useMemo } from 'react'
import { isNil } from 'ramda'
import Link from 'next/link'

import useToast from '~/src/components/generic/Toast/hook/useToast'

import {
  NotificationDisplayType,
  Notification,
} from '~/src/components/shared/Notification/types'
import useClickNotification from '~/src/components/shared/Notification/hooks/useClickNotification'
import useMarkNotificationsAsRead from '~/src/components/shared/Notification/hooks/useMarkNotificationsAsRead'
import { getNotificationTimeLapsed } from '~/src/components/shared/Notification/helpers'
import NotificationHeader from '~/src/components/shared/Notification/components/NotificationHeader'
import NotificationMeta from '~/src/components/shared/Notification/components/NotificationMeta'
import NotificationStatus from '~/src/components/shared/Notification/components/NotificationStatus'

import { MixpanelEvent, track } from '~/clients/mixpanelClient'
import { tw } from '~/lib/utils'

type Props = {
  notification: Notification
  displayType: NotificationDisplayType
  timeSinceNotified?: string
}

export const NotificationMessage: FC<Props> = ({
  notification,
  displayType,
  timeSinceNotified,
}) => {
  const toast = useToast()
  const [createHref] = useClickNotification()
  const [markNotificationsAsRead] = useMarkNotificationsAsRead()

  const href = useMemo(() => createHref(notification), [createHref, notification])
  const unread = isNil(notification.readAt)

  if (displayType === 'dropdown' && !notification.timestamp) return null

  const trackNotificationSelected = (notification: Notification) => {
    track(MixpanelEvent.NotificationSelected, {
      source: 'Toast',
      entryPoint: 'Toast',
      notificationType: notification.type,
      notificationTier: notification.tier,
      age: getNotificationTimeLapsed(notification.timestamp),
      load: notification.load?.ryderNumber,
      driver: notification.driver?.fullname,
    })
  }

  const onNotificationClickHandler = (notification: Notification) => {
    trackNotificationSelected(notification)

    if (isNil(notification.readAt)) {
      markNotificationsAsRead([notification])
    }

    toast.remove(notification.id)
  }

  return (
    <Link
      aria-label={`View notification for load ${notification.load?.ryderNumber ?? ''}`}
      className={tw(
        'block hover:cursor-pointer',
        displayType === 'dropdown' ? 'w-80' : 'w-68'
      )}
      data-testid="notification-message"
      href={href}
      onClick={() => onNotificationClickHandler(notification)}
    >
      <div
        className={tw(
          'flex items-center justify-between',
          displayType === 'dropdown' ? 'w-80' : 'w-68'
        )}
      >
        <div className={tw(displayType === 'dropdown' && 'pl-3')}>
          <NotificationHeader notification={notification} />
          <NotificationMeta
            displayType={displayType}
            notification={notification}
            unread={unread}
          />
        </div>

        {displayType === 'dropdown' && (
          <NotificationStatus timeSinceNotified={timeSinceNotified} unread={unread} />
        )}
      </div>
    </Link>
  )
}
